import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ChevronRight,
  GitMerge,
  LayoutGrid,
  Sparkles,
  TrendingDown,
} from 'lucide-react';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';
import {
  LearnedList,
  deriveGaps,
  deriveStrengths,
  type GovernStance,
} from '../components/fleet/FleetSections';
import { GovernanceSection } from '../components/fleet/GovernanceSection';
import { FleetClientService, type FleetInsightsDoc } from '../services/fleetClientService';
import { PolicyService } from '../services/policyService';

function SectionCard({
  icon: Icon,
  tint,
  title,
  subtitle,
  children,
}: {
  icon: React.ElementType;
  tint: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="p-5 rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white dark:bg-gray-900/40 shadow-sm transition-shadow hover:shadow-md">
      <div className={`flex items-center gap-2.5 ${subtitle ? 'mb-1' : 'mb-4'}`}>
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${tint}`}>
          <Icon className="w-4 h-4" />
        </span>
        <h2 className="font-semibold text-gray-900 dark:text-white">{title}</h2>
      </div>
      {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">{subtitle}</p>}
      {children}
    </section>
  );
}

function AdoptionRow({
  orgId,
  slug,
  name,
  count,
  fleetSize,
}: {
  orgId: string;
  slug: string;
  name: string;
  count: number;
  fleetSize: number;
}) {
  return (
    <Link
      to={`/orgs/${orgId}/practice/${encodeURIComponent(slug)}?name=${encodeURIComponent(name)}`}
      className="flex justify-between items-center gap-2 group rounded-lg px-1.5 py-1 -mx-1.5 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/40"
    >
      <span className="flex items-center gap-1 min-w-0 text-gray-800 dark:text-gray-200 group-hover:text-teal-700 dark:group-hover:text-teal-400">
        <ChevronRight className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-100" />
        <span className="truncate">{name}</span>
      </span>
      <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400 tabular-nums">
        {count}/{fleetSize}
      </span>
    </Link>
  );
}

function ReportSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="mt-8 mb-4 h-40 rounded-3xl bg-gray-100 dark:bg-gray-800/60" />
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div className="h-56 rounded-2xl bg-gray-100 dark:bg-gray-800/60" />
        <div className="h-56 rounded-2xl bg-gray-100 dark:bg-gray-800/60" />
      </div>
      <div className="h-64 rounded-2xl bg-gray-100 dark:bg-gray-800/60 mb-4" />
      <div className="h-48 rounded-2xl bg-gray-100 dark:bg-gray-800/60" />
    </div>
  );
}

export const FleetReportPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [insights, setInsights] = useState<FleetInsightsDoc | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [governed, setGoverned] = useState<Set<string>>(new Set());
  // Bumping this re-syncs both the govern buttons and the GovernanceSection.
  const [version, setVersion] = useState(0);

  const refreshGoverned = useCallback(async () => {
    if (!id) return;
    try {
      const comp = await PolicyService.compliance(id);
      setGoverned(new Set(comp.data.byPolicy.map((b) => b.entitySlug)));
    } catch {
      setGoverned(new Set());
    }
  }, [id]);

  const govern = useCallback(
    async (slug: string, name: string, stance: GovernStance) => {
      if (!id) return;
      await PolicyService.upsert(id, { entitySlug: slug, entityName: name, stance });
      setVersion((v) => v + 1);
      await refreshGoverned();
    },
    [id, refreshGoverned],
  );

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const res = await FleetClientService.orgInsights(id);
      setInsights(res.data.insights);
      setGeneratedAt(res.data.generatedAt);
      await refreshGoverned();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [id, refreshGoverned]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50/40 dark:bg-transparent">
        <Navbar pageTitle="Fleet report" showDivider={true} />
        <main className="pt-(--navbar-offset) max-w-4xl mx-auto px-4 pb-20">
          <ReportSkeleton />
        </main>
      </div>
    );
  }

  if (error || !insights) {
    return (
      <div className="min-h-screen bg-gray-50/40 dark:bg-transparent">
        <Navbar pageTitle="Fleet report" showDivider={true} />
        <div className="text-center pt-32 text-red-500">{error || 'No report available'}</div>
      </div>
    );
  }

  const strengths = deriveStrengths(insights);
  const gaps = deriveGaps(insights);
  const tools = Object.entries(insights.adoption)
    .filter(([, a]) => a.kind === 'tool' || a.kind === 'practice')
    .sort((a, b) => b[1].count - a[1].count);
  const catalog = tools.map(([slug, a]) => ({ slug, name: a.name }));
  // Readable policy name for an issue's practice — use the detected name
  // if known, else humanize the slug (missing practices aren't in adoption).
  const catalogName = (slug: string, fallbackTitle: string) =>
    insights.adoption[slug]?.name ??
    (slug ? slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : fallbackTitle);

  return (
    <div className="min-h-screen bg-gray-50/40 dark:bg-transparent">
      <Navbar pageTitle={`${insights.org} — Fleet report`} showDivider={true} />
      <main className="pt-(--navbar-offset) max-w-4xl mx-auto px-4 pb-20">
        {/* Hero header */}
        <div className="relative mt-8 mb-4 overflow-hidden rounded-3xl border border-gray-200/70 dark:border-gray-800 bg-gradient-to-br from-teal-50 via-white to-white dark:from-teal-950/30 dark:via-gray-900/40 dark:to-gray-900/40 p-6 sm:p-8">
          <div className="relative">
            <Link
              to={`/orgs/${id}/fleet`}
              className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-2"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Fleet health
            </Link>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              What Autar learned — full report
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {insights.org} · {insights.fleet_size} repos · {insights.exemplar_size} exemplar repos
              compared
              {generatedAt ? ` · generated ${new Date(generatedAt).toLocaleDateString()}` : ''}
            </p>
          </div>
        </div>

        <div className="space-y-4 text-sm">
          <div className="grid sm:grid-cols-2 gap-4">
            <SectionCard
              icon={Sparkles}
              tint="bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400"
              title="Strengths"
            >
              <LearnedList items={strengths} onGovern={govern} governed={governed} />
            </SectionCard>
            <SectionCard
              icon={TrendingDown}
              tint="bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400"
              title="Gaps vs open source"
            >
              <LearnedList items={gaps} onGovern={govern} governed={governed} />
            </SectionCard>
          </div>

          {id && (
            <GovernanceSection
              orgId={id}
              catalog={catalog}
              version={version}
              onChange={() => {
                setVersion((v) => v + 1);
                void refreshGoverned();
              }}
            />
          )}

          <SectionCard
            icon={AlertTriangle}
            tint="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400"
            title={`Systemic issues (${insights.systemic_issues.length})`}
            subtitle="Make a fix mandatory across the fleet — every repo missing it becomes a policy violation."
          >
            <ul className="space-y-1.5 text-sm">
              {insights.systemic_issues.map((iss) => {
                const isMandatory = governed.has(iss.entity_slug);
                return (
                  <li
                    key={`${iss.entity_slug}-${iss.title}`}
                    className="flex items-center gap-2 justify-between rounded-lg px-1.5 py-1 -mx-1.5 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/40"
                  >
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="text-xs text-gray-400 w-10 shrink-0 tabular-nums">
                        {iss.affected_count}×
                      </span>
                      <span className="text-gray-800 dark:text-gray-200 truncate">{iss.title}</span>
                      <span className="text-xs text-gray-400 shrink-0">[{iss.severity}]</span>
                    </div>
                    {isMandatory ? (
                      <span className="shrink-0 text-[11px] px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300">
                        mandatory
                      </span>
                    ) : (
                      <button
                        onClick={() =>
                          govern(
                            iss.entity_slug,
                            catalogName(iss.entity_slug, iss.title),
                            'required',
                          )
                        }
                        className="shrink-0 text-[11px] px-2 py-0.5 rounded-full border border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-400 transition-colors hover:bg-teal-100 dark:hover:bg-teal-900/40 active:scale-[.97]"
                        title="Require this fix across the fleet"
                      >
                        Make mandatory
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </SectionCard>

          <SectionCard
            icon={LayoutGrid}
            tint="bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400"
            title="Adoption across the fleet"
            subtitle="Click a practice to see which repositories use it."
          >
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-0.5 text-sm">
              {tools.map(([slug, a]) => (
                <AdoptionRow
                  key={slug}
                  orgId={id!}
                  slug={slug}
                  name={a.name}
                  count={a.count}
                  fleetSize={insights.fleet_size}
                />
              ))}
            </div>
          </SectionCard>

          <SectionCard
            icon={GitMerge}
            tint="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
            title={`Co-occurrence patterns (top ${Math.min(10, insights.cooccurrence.length)})`}
          >
            <ul className="space-y-1.5 text-sm">
              {insights.cooccurrence.slice(0, 10).map((r) => (
                <li key={`${r.if}-${r.then}`} className="text-gray-600 dark:text-gray-400">
                  Repos with <span className="text-gray-800 dark:text-gray-200">{r.if}</span> also
                  use <span className="text-gray-800 dark:text-gray-200">{r.then}</span>{' '}
                  <span className="tabular-nums">{Math.round(r.confidence * 100)}%</span> (support{' '}
                  <span className="tabular-nums">{r.support}</span>)
                </li>
              ))}
            </ul>
          </SectionCard>
        </div>
      </main>
      <Footer />
    </div>
  );
};
